import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

/**
 * THE tab primitive (WAI-ARIA Authoring Practices, Tabs pattern). Nothing in the SPA should write
 * `role="tab"` / `role="tablist"` / `role="tabpanel"` by hand — a tab strip is only a tab strip to
 * assistive technology once the roving tabindex, the arrow keys and the panel's `aria-labelledby`
 * are all there, and each of those is easy to leave out one screen at a time.
 *
 * What it guarantees:
 *   - exactly ONE tab in the page's tab order (the selected one); the rest are `tabindex="-1"`, so
 *     Tab moves past the whole strip and the arrows move WITHIN it (APG roving tabindex);
 *   - ArrowLeft/ArrowRight (ArrowUp/ArrowDown when vertical) move and wrap, Home/End jump to the
 *     ends — activation follows focus, which is the APG default for panels that are already in the
 *     DOM and cheap to show;
 *   - every panel is `aria-labelledby` its tab and `aria-controls`-ed from it;
 *   - the selected tab is marked by WEIGHT and INK as well as the coral rule (Brändiehdotus v2:
 *     never colour alone — a monochrome or colour-blind reader still sees which tab is current).
 *
 * SCOPING — the bug this exists to prevent. Two tab groups now live on different M3 screens, and the
 * mockup's global `selectTab` hid the other group's panel every time it ran. Two mechanisms keep a
 * group to itself, and neither is a registry:
 *   1. IDS come from `useId()` on the root, so `${baseId}-tab-<value>` cannot collide with another
 *      group's even when both groups use the same tab values ("all", "active", …).
 *   2. The KEYBOARD walk starts at `e.currentTarget.closest('[role="tablist"]')` and queries only
 *      inside it — never `document.querySelectorAll('[role="tab"]')`, which is the document-wide
 *      query that made the mockup's groups interfere.
 */

interface TabsContextValue {
  baseId: string;
  selected: string;
  select: (value: string) => void;
  orientation: "horizontal" | "vertical";
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error(`<${component}> must be rendered inside <Tabs> — it takes its group's ids and selection from it.`);
  }
  return ctx;
}

// Internal to the group: ids are derived, never passed in, so no caller can accidentally give two
// groups the same one. (Kept unexported so the module exports components only — react-refresh.)
function tabId(baseId: string, value: string) {
  return `${baseId}-tab-${value}`;
}

function tabPanelId(baseId: string, value: string) {
  return `${baseId}-panel-${value}`;
}

export interface TabsProps {
  /** Tab selected when the group mounts, uncontrolled. Ignored when `value` is given. */
  defaultValue?: string;
  /** Controlled selection — pass with `onValueChange` when the route/query string owns the tab. */
  value?: string;
  /** Fires on every selection change, controlled or not. */
  onValueChange?: (value: string) => void;
  /** Arrow keys that move between tabs: Left/Right (default) or Up/Down. */
  orientation?: "horizontal" | "vertical";
  /** Extra class(es) on the group wrapper, alongside the shared `tabs`. */
  className?: string;
  children: ReactNode;
}

/** The group. Owns the selection and the id namespace; renders a plain wrapper, no role of its own. */
export function Tabs({
  defaultValue = "",
  value,
  onValueChange,
  orientation = "horizontal",
  className,
  children,
}: TabsProps) {
  const baseId = useId();
  const [internal, setInternal] = useState(defaultValue);
  const controlled = value !== undefined;
  const selected = controlled ? value : internal;

  const select = useCallback(
    (next: string) => {
      if (!controlled) setInternal(next);
      onValueChange?.(next);
    },
    [controlled, onValueChange],
  );

  const ctx = useMemo<TabsContextValue>(
    () => ({ baseId, selected, select, orientation }),
    [baseId, selected, select, orientation],
  );

  return (
    <TabsContext.Provider value={ctx}>
      <div className={className ? `tabs ${className}` : "tabs"}>{children}</div>
    </TabsContext.Provider>
  );
}

export interface TabListProps {
  /** What the strip is a set of choices FOR. Every tablist names itself (WCAG 2.1 AA 4.1.2). */
  label: string;
  className?: string;
  /** `data-testid` on the strip — what the E2E suite locates. */
  testId?: string;
  children: ReactNode;
}

/** The strip. `[role="tablist"]` is also the boundary the keyboard walk is scoped by. */
export function TabList({ label, className, testId, children }: TabListProps) {
  const { orientation } = useTabsContext("TabList");
  return (
    <div
      role="tablist"
      aria-label={label}
      aria-orientation={orientation}
      className={className ? `tabs__list ${className}` : "tabs__list"}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export interface TabProps {
  /** Identifies the tab within its group, and pairs it with the `<TabPanel>` of the same value. */
  value: string;
  className?: string;
  testId?: string;
  children: ReactNode;
}

/**
 * One tab. A real `<button>`, so it is operable by Enter/Space and by the pointer without any
 * handler of ours; the arrow keys are what this adds on top.
 */
export function Tab({ value, className, testId, children }: TabProps) {
  const { baseId, selected, select, orientation } = useTabsContext("Tab");
  const isSelected = selected === value;

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    const prevKey = orientation === "vertical" ? "ArrowUp" : "ArrowLeft";
    const nextKey = orientation === "vertical" ? "ArrowDown" : "ArrowRight";
    if (!["Home", "End", prevKey, nextKey].includes(e.key)) return;

    // Scoped to THIS strip. A document-wide `[role="tab"]` query is the bug the primitive exists to
    // prevent — it would walk straight into the other group's tabs.
    const list = e.currentTarget.closest('[role="tablist"]');
    if (!list) return;
    const tabs = Array.from(list.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'));
    const from = tabs.indexOf(e.currentTarget);
    if (from === -1 || tabs.length === 0) return;

    let to: number;
    if (e.key === "Home") to = 0;
    else if (e.key === "End") to = tabs.length - 1;
    else if (e.key === prevKey) to = (from - 1 + tabs.length) % tabs.length;
    else to = (from + 1) % tabs.length;

    e.preventDefault();
    const target = tabs[to];
    target.focus();
    // Activation follows focus (APG's automatic activation). `data-tab-value` carries the value
    // because the walk is over DOM nodes, not over React children.
    const nextValue = target.dataset.tabValue;
    if (nextValue !== undefined) select(nextValue);
  }

  return (
    <button
      type="button"
      role="tab"
      id={tabId(baseId, value)}
      aria-selected={isSelected}
      aria-controls={tabPanelId(baseId, value)}
      // Roving tabindex: only the selected tab is in the page's tab order (APG).
      tabIndex={isSelected ? 0 : -1}
      data-tab-value={value}
      className={className ? `tabs__tab ${className}` : "tabs__tab"}
      data-testid={testId}
      onClick={() => select(value)}
      onKeyDown={onKeyDown}
    >
      {children}
    </button>
  );
}

export interface TabPanelProps {
  /** Pairs the panel with the `<Tab>` of the same value. */
  value: string;
  className?: string;
  testId?: string;
  children: ReactNode;
}

/**
 * One panel. `hidden` (not merely unmounted) when its tab is not selected, so nothing inside it is
 * reachable by the keyboard or by a screen reader while it is away.
 *
 * `tabIndex={0}` is deliberate and comes from the APG: a panel whose content has no focusable
 * element of its own would otherwise be unreachable by keyboard. It IS therefore a tab stop, and it
 * keeps the app's global focus ring when a keyboard puts focus there — see the
 * `:focus:not(:focus-visible)` rule in styles/components.css (WCAG 2.1 AA 2.4.7).
 */
export function TabPanel({ value, className, testId, children }: TabPanelProps) {
  const { baseId, selected } = useTabsContext("TabPanel");
  const isSelected = selected === value;
  return (
    <div
      role="tabpanel"
      id={tabPanelId(baseId, value)}
      aria-labelledby={tabId(baseId, value)}
      tabIndex={0}
      hidden={!isSelected}
      className={className ? `tabs__panel ${className}` : "tabs__panel"}
      data-testid={testId}
    >
      {isSelected ? children : null}
    </div>
  );
}
